// Port of SegmentedTabs (GroundTeam/Views/Components/Cards.swift): uppercase pill segments on a tonal track with a
// filled navy indicator under the selected one. Semantically a radio group (one choice, arrows move and select),
// which is what the period switches and list scopes are.

import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn';

export interface SegmentOption<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  /** Optional count shown after the label ("OVERDUE (12)"). */
  count?: number;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string> {
  value: V;
  onChange: (value: V) => void;
  options: readonly SegmentOption<V>[];
  /** Accessible name of the group ("Period"). */
  label: string;
  size?: 'sm' | 'md';
  /** Stretch segments to fill the width (iOS default); otherwise each hugs its label. */
  fullWidth?: boolean;
  /** iOS shows the labels in caps; tables and dense toolbars may prefer normal case. */
  uppercase?: boolean;
  /** Show only each option's icon; its label (a string) becomes the segment's accessible name and hover text. */
  iconOnly?: boolean;
  className?: string;
}

export function SegmentedControl<V extends string>({
  value,
  onChange,
  options,
  label,
  size = 'md',
  fullWidth = false,
  uppercase = true,
  iconOnly = false,
  className,
}: SegmentedControlProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);

  const move = (from: number, delta: number) => {
    if (enabled.length === 0) return;
    const at = enabled.indexOf(from);
    const next = enabled[(at + delta + enabled.length) % enabled.length]!;
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    // Arrows select as they move, so a held key would cycle the choice at key-repeat speed (for the Appearance
    // switch, flashing the whole page between light and dark). One press, one step.
    if (event.repeat && ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move(index, -1);
        break;
      case 'Home':
        event.preventDefault();
        if (enabled.length) move(enabled[0]!, 0);
        break;
      case 'End':
        event.preventDefault();
        if (enabled.length) move(enabled[enabled.length - 1]!, 0);
        break;
    }
  };

  const selectedIndex = options.findIndex((o) => o.value === value);
  // Roving tab stop: the selected segment, or the first enabled one when nothing matches.
  const tabStop = selectedIndex >= 0 && !options[selectedIndex]!.disabled ? selectedIndex : (enabled[0] ?? -1);

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-flex items-stretch gap-0.5 rounded-full bg-surface-3/80 p-[3px] shadow-[inset_0_0_0_0.5px_var(--gt-line)]',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        const name = iconOnly && typeof option.label === 'string' ? option.label : undefined;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={name}
            title={name}
            disabled={option.disabled}
            tabIndex={index === tabStop ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full font-bold whitespace-nowrap',
              'transition-[background-color,color,box-shadow] duration-200 disabled:opacity-40 [&_svg]:size-3.5',
              uppercase ? 'tracking-[0.05em] uppercase' : 'tracking-normal',
              size === 'sm' ? 'h-7 text-[11px]' : 'h-8 text-xs',
              iconOnly ? 'px-2.5' : size === 'sm' ? 'px-3' : 'px-4',
              fullWidth && 'flex-1',
              selected
                ? 'bg-navy text-white shadow-[0_3px_6px_rgb(16_42_94/0.25)] dark:bg-[#2a4a8f]'
                : 'text-fg-muted hover:text-fg',
            )}
          >
            {option.icon}
            {!iconOnly && <span className="truncate">{option.label}</span>}
            {option.count !== undefined && (
              <span className={cn('tabular-nums', selected ? 'text-white/80' : 'text-fg-subtle')}>
                ({option.count.toLocaleString()})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
